import { useCallback } from 'react';
import { useFileReportsStore } from '@/store';
import { ReportsService } from '@/services/reports.service';
import type { FileReport } from '@/types/reports.types';

/**
 * `admin` (REPORTS_FILES_ADMIN) lists from `/reports/files/manage` and fills the category filter
 * from every active category, so admins see hidden and pool-restricted files too.
 */
export const useFileReports = ({ admin = false }: { admin?: boolean } = {}) => {
  // Use individual selectors for reactive state
  const files = useFileReportsStore((state) => state.files);
  const isLoading = useFileReportsStore((state) => state.isLoading);
  const isLoadingMore = useFileReportsStore((state) => state.isLoadingMore);
  const error = useFileReportsStore((state) => state.error);
  const filters = useFileReportsStore((state) => state.filters);
  const hasNextPage = useFileReportsStore((state) => state.hasNextPage);
  const totalCount = useFileReportsStore((state) => state.totalCount);

  const loadReports = useCallback(
    async (reset = false) => {
      const store = useFileReportsStore.getState();
      try {
        store.setError(null);
        if (reset) {
          store.setLoading(true);
          store.setPagination({ pageNumber: 1 });
        }

        const currentPagination = reset ? { ...store.pagination, pageNumber: 1 } : store.pagination;
        const response = await ReportsService.getFileReports(currentPagination, store.filters, {
          admin,
        });

        if (reset) {
          store.setFiles(response.data);
        } else {
          store.addFiles(response.data);
        }

        store.setHasNextPage(response.hasNextPage);
        store.setTotalCount(response.totalCount);
      } catch (err) {
        store.setError(err instanceof Error ? err.message : 'Failed to load reports');
      } finally {
        store.setLoading(false);
        store.setLoadingMore(false);
      }
    },
    [admin]
  );

  const loadMore = useCallback(async () => {
    const store = useFileReportsStore.getState();
    if (!store.hasNextPage || store.isLoadingMore) return;

    store.setLoadingMore(true);
    store.setPagination({ pageNumber: store.pagination.pageNumber + 1 });
    await loadReports(false);
  }, [loadReports]);

  const loadCategories = useCallback(async () => {
    try {
      if (admin) {
        const options = await ReportsService.getCategoryOptions();
        const store = useFileReportsStore.getState();
        store.setCategoryOptions(options);
        store.setCategories(
          [...new Set(options.map((o) => o.pageTypeName))].sort((a, b) => a.localeCompare(b))
        );
        return;
      }
      const cats = await ReportsService.getCategories();
      useFileReportsStore.getState().setCategories(cats);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }, [admin]);

  const handleRefresh = useCallback(() => {
    return loadReports(true);
  }, [loadReports]);

  const handleDownload = useCallback(async (file: FileReport) => {
    await ReportsService.downloadFile(file.fileId, file.fileName);
  }, []);

  const updateFilters = useCallback(
    (newFilters: { search?: string; category?: string; includeHidden?: boolean }) => {
      useFileReportsStore.getState().setFilters(newFilters);
    },
    []
  );

  return {
    // State
    files,
    isLoading,
    isLoadingMore,
    error,
    filters,
    hasNextPage,
    totalCount,

    // Actions
    loadReports,
    loadMore,
    loadCategories,
    handleRefresh,
    handleDownload,
    updateFilters,
  };
};
